/* ==========================================================================
   Bundle PDP hero — gallery, shade picker and add-to-cart.
   No dependencies. Loaded with `defer` by sections/bundle-hero.liquid.

   Progressive enhancement: with JS off the section still renders a native
   <form action="/cart/add"> with a real variant <select>, so the bundle can
   still be bought.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTOR = '[data-bundle-hero]';

  function parseConfig(root) {
    var node = root.querySelector('[data-bundle-hero-config]');
    if (!node) return {};
    try {
      return JSON.parse(node.textContent) || {};
    } catch (error) {
      return {};
    }
  }

  function norm(value) {
    return String(value == null ? '' : value).trim().toLowerCase();
  }

  /* Every string a swatch is allowed to match a variant on.

     Two admin structures are both valid for this kind of kit:
       * one option ("Kit Choice") whose values are "Blonde / Light Highlighter"
       * two options ("Brow Shade" + "Highlighter")
     Splitting the title on "/" as well as reading the option values means a
     swatch can carry just "Blonde" and resolve under either structure. */
  function matchKeys(variant) {
    var keys = (variant.options || []).map(norm);
    var title = norm(variant.title);

    keys.push(title);
    title.split('/').forEach(function (part) {
      var trimmed = part.trim();
      if (trimmed) keys.push(trimmed);
    });

    return keys;
  }

  function BundleHero(root) {
    this.root = root;
    this.config = parseConfig(root);
    this.variants = this.config.variants || [];

    this.slides = root.querySelector('[data-bh-slides]');
    this.thumbs = Array.prototype.slice.call(root.querySelectorAll('[data-bh-thumb]'));
    this.revealButtons = Array.prototype.slice.call(root.querySelectorAll('[data-bh-reveal]'));
    this.jumpLinks = Array.prototype.slice.call(root.querySelectorAll('[data-bh-jump]'));
    this.shade = root.querySelector('[data-bh-shade]');
    this.kitName = root.querySelector('[data-bh-kit-name]');
    this.select = root.querySelector('[data-bh-variant-select]');
    this.form = root.querySelector('[data-bh-form]');
    this.submit = root.querySelector('[data-bh-submit]');
    this.errorNode = root.querySelector('[data-bh-error]');
    this.cartLink = root.querySelector('[data-bh-cart]');
    this.cartCount = root.querySelector('[data-bh-cart-count]');

    /* One entry per rendered swatch group ("brow", "highlighter", …). The
       readouts start on their "nothing chosen yet" copy, which is kept so a
       selection can be undone — see clearSwatches. */
    this.groups = Array.prototype.slice.call(root.querySelectorAll('[data-bh-group]')).map(function (node) {
      var value = node.querySelector('[data-bh-group-value]');
      var desc = node.querySelector('[data-bh-group-desc]');

      return {
        key: node.dataset.bhGroup,
        node: node,
        value: value,
        desc: desc,
        valuePlaceholder: value ? value.textContent : '',
        descPlaceholder: desc ? desc.textContent : '',
        swatches: Array.prototype.slice.call(node.querySelectorAll('[data-bh-swatch]')),
        selected: null
      };
    });

    this.nudgeTimer = null;

    this.initGallery();
    this.initJump();
    this.initReveal();
    this.initSwatches();
    this.initForm();
    this.initCart();
  }

  /* ----------------------------------------------------------- gallery ---- */

  BundleHero.prototype.initGallery = function () {
    var self = this;
    if (!this.slides || !this.thumbs.length) return;

    this.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        self.goToSlide(thumb.dataset.bhThumb);
      });
    });

    /* The arrows step the stage rather than the rail: setActiveThumb scrolls
       the new thumbnail into view, so one action moves both. */
    Array.prototype.slice.call(this.root.querySelectorAll('[data-bh-thumb-step]')).forEach(function (button) {
      button.addEventListener('click', function () {
        self.stepSlide(parseInt(button.dataset.bhThumbStep, 10) || 0);
      });
    });

    // Keep the thumbnail state in sync when the stage is swiped.
    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && !self.thumbSyncHeld) self.setActiveThumb(entry.target.dataset.bhSlide);
        });
      },
      { root: this.slides, threshold: 0.6 }
    );

    this.slides.querySelectorAll('[data-bh-slide]').forEach(function (slide) {
      observer.observe(slide);
    });
  };

  BundleHero.prototype.goToSlide = function (index, behavior) {
    var slide = this.slides && this.slides.querySelector('[data-bh-slide="' + index + '"]');
    if (!slide) return;

    /* Claim the outline for the target thumbnail before starting the scroll,
       and mute the observer until the stage settles — otherwise every slide
       the animation passes over takes the outline in turn and it appears to
       travel down the rail instead of jumping. */
    this.holdThumbSync();
    this.setActiveThumb(String(index));
    this.slides.scrollTo({
      left: slide.offsetLeft - this.slides.offsetLeft,
      behavior: behavior || 'smooth'
    });
  };

  /* Move one slide either way, wrapping at both ends so neither arrow is ever
     a dead control. A wrap crosses the whole strip, so it jumps rather than
     animating the entire width past the shopper. */
  BundleHero.prototype.stepSlide = function (delta) {
    if (!this.slides || !this.thumbs.length) return;

    var current = 0;
    this.thumbs.forEach(function (thumb, index) {
      if (thumb.getAttribute('aria-current') === 'true') current = index;
    });

    var count = this.thumbs.length;
    var next = (current + delta + count) % count;
    var wrapped = Math.abs(next - current) > 1;

    this.goToSlide(next, wrapped ? 'auto' : 'smooth');
  };

  /* Mute observer-driven thumbnail updates until the stage stops scrolling.
     A swipe that interrupts the animation keeps firing scroll events, so the
     hold simply extends and then resolves against wherever the stage landed. */
  BundleHero.prototype.holdThumbSync = function () {
    var self = this;

    if (!this.thumbSyncDefer) {
      this.thumbSyncDefer = function () {
        clearTimeout(self.thumbSyncTimer);
        self.thumbSyncTimer = setTimeout(function () {
          self.slides.removeEventListener('scroll', self.thumbSyncDefer);
          self.thumbSyncHeld = false;
          var index = self.visibleSlideIndex();
          if (index != null) self.setActiveThumb(index);
        }, 120);
      };
    }

    this.thumbSyncHeld = true;
    this.slides.addEventListener('scroll', this.thumbSyncDefer, { passive: true });
    /* Backstop for a click whose slide is already in view: nothing scrolls, so
       no scroll event ever arrives to start the release timer. */
    this.thumbSyncDefer();
  };

  /* The slide sitting closest to the middle of the stage viewport. */
  BundleHero.prototype.visibleSlideIndex = function () {
    var self = this;
    var centre = this.slides.scrollLeft + this.slides.clientWidth / 2;
    var closest = null;
    var shortest = Infinity;

    this.slides.querySelectorAll('[data-bh-slide]').forEach(function (slide) {
      var offset = slide.offsetLeft - self.slides.offsetLeft;
      var distance = Math.abs(offset + slide.offsetWidth / 2 - centre);
      if (distance < shortest) {
        shortest = distance;
        closest = slide;
      }
    });

    return closest ? closest.dataset.bhSlide : null;
  };

  BundleHero.prototype.setActiveThumb = function (index) {
    var active = null;
    this.thumbs.forEach(function (thumb) {
      var isActive = thumb.dataset.bhThumb === index;
      thumb.setAttribute('aria-current', String(isActive));
      if (isActive) active = thumb;
    });

    // Keep the active thumbnail visible without scrolling the document. Using
    // scrollIntoView here also moves the page vertically when the site header
    // is present above the bundle hero on product templates.
    if (active) {
      var viewport = active.closest('.bundle-hero__thumbs-viewport');
      if (!viewport) return;

      var viewportRect = viewport.getBoundingClientRect();
      var activeRect = active.getBoundingClientRect();
      var nextLeft = viewport.scrollLeft;

      if (activeRect.left < viewportRect.left) {
        nextLeft -= viewportRect.left - activeRect.left;
      } else if (activeRect.right > viewportRect.right) {
        nextLeft += activeRect.right - viewportRect.right;
      }

      if (nextLeft !== viewport.scrollLeft) {
        viewport.scrollTo({ left: nextLeft, behavior: 'smooth' });
      }
    }
  };

  /* ------------------------------------------------- jump to the picker ---- */

  /* The hero's main button is a real link when the picker lives in its own
     section, so it still works with JS off. This upgrades it to a smooth
     scroll and, crucially, falls back to whatever shade picker is actually on
     the page when the anchor in the setting no longer matches a section id —
     renaming or duplicating the picker section can't break the button. */
  BundleHero.prototype.initJump = function () {
    this.jumpLinks.forEach(function (link) {
      link.addEventListener('click', function (event) {
        var href = link.getAttribute('href') || '';
        var target = null;

        // An id starting with a digit is not a valid selector — hence the try.
        try {
          if (href.charAt(0) === '#' && href.length > 1) target = document.querySelector(href);
        } catch (error) {
          target = null;
        }

        if (!target) target = document.querySelector('[data-bh-shade]');
        // Nothing to jump to: leave the href to do whatever it was going to do.
        if (!target) return;

        event.preventDefault();

        var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });

        // Land on the first swatch so keyboard users continue from the step
        // the button sent them to. preventScroll keeps the animation intact.
        var swatch = target.querySelector('[data-bh-swatch]');
        if (swatch && swatch.focus) swatch.focus({ preventScroll: true });
      });
    });
  };

  /* ------------------------------------------------- reveal shade picker ---- */

  BundleHero.prototype.initReveal = function () {
    var self = this;
    if (!this.shade) return;

    this.revealButtons.forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();
        self.reveal();
      });
    });
  };

  BundleHero.prototype.reveal = function () {
    var wasCollapsed = this.root.classList.contains('bundle-hero--collapsed');
    this.root.classList.remove('bundle-hero--collapsed');

    this.revealButtons.forEach(function (button) {
      button.setAttribute('aria-expanded', 'true');
    });

    var first = this.groups[0];
    var target = (first && first.swatches[0]) || this.shade;

    // Let the browser paint the newly shown block before scrolling to it.
    window.requestAnimationFrame(function () {
      if (!target) return;
      target.scrollIntoView({ behavior: wasCollapsed ? 'auto' : 'smooth', block: 'center' });
      if (target.focus) target.focus({ preventScroll: true });
    });
  };

  /* ------------------------------------------------------------ swatches ---- */

  BundleHero.prototype.initSwatches = function () {
    var self = this;

    this.groups.forEach(function (group) {
      group.swatches.forEach(function (swatch) {
        swatch.addEventListener('click', function () {
          self.selectSwatch(group, swatch);
        });
      });
    });

    if (!this.select) return;

    this.select.addEventListener('change', function (event) {
      // Ignore the synthetic event we dispatch ourselves after resolving.
      if (event.bhInternal) return;
      self.syncFromSelect();
    });

    this.updateKitName();

    // Only treat the select as "already chosen" when the shopper actually
    // arrived on a variant deep link (?variant=… from an ad or a shade quiz).
    // Otherwise the first option would silently count as a shade choice.
    var deepLink = null;
    try {
      deepLink = new URLSearchParams(window.location.search).get('variant');
    } catch (error) {
      deepLink = null;
    }
    if (deepLink && deepLink === this.select.value) this.syncFromSelect();
  };

  BundleHero.prototype.selectSwatch = function (group, swatch) {
    group.swatches.forEach(function (item) {
      item.setAttribute('aria-pressed', String(item === swatch));
    });
    group.selected = swatch;

    if (group.value) {
      group.value.textContent = swatch.dataset.swatchName || '';
      group.value.dataset.selected = 'true';
    }

    if (group.desc && swatch.dataset.swatchDesc) {
      group.desc.textContent = swatch.dataset.swatchDesc;
      group.desc.classList.remove('is-nudged');
    }

    this.resolveVariant();
    this.clearError();
    this.track('shade_selected', { group: group.key, shade: swatch.dataset.swatchName });
  };

  /* A variant wins when it satisfies every group that has a selection —
     matching either one of its option values or its full title. That way a
     single brow group picks the paired highlighter on its own, and adding a
     highlighter group narrows the same list further. */
  BundleHero.prototype.resolveVariant = function () {
    var wanted = this.groups
      .filter(function (group) {
        return group.selected && group.selected.dataset.optionValue;
      })
      .map(function (group) {
        return norm(group.selected.dataset.optionValue);
      });

    if (wanted.length) {
      var match = this.variants.filter(function (variant) {
        var keys = matchKeys(variant);
        return wanted.every(function (value) {
          return keys.indexOf(value) !== -1;
        });
      });

      // Prefer something buyable when more than one variant fits.
      var choice = match.filter(function (variant) { return variant.available; })[0] || match[0];

      if (choice && this.select && this.select.value !== String(choice.id)) {
        this.select.value = String(choice.id);
        var event = new Event('change', { bubbles: true });
        event.bhInternal = true;
        this.select.dispatchEvent(event);
      }
    }

    this.updateKitName();
    this.setReady(this.isReady());
  };

  /* Mirror the <select> back onto the swatches — covers deep links, back
     navigation and the native picker being used directly. */
  BundleHero.prototype.syncFromSelect = function () {
    if (!this.select) return;

    var self = this;
    var variant = this.variants.filter(function (item) {
      return String(item.id) === self.select.value;
    })[0];

    this.updateKitName();

    if (!variant) {
      /* Back on the placeholder option: drop the swatch selections with it, so
         the shopper has to choose again before the button unlocks. An id we
         simply do not know is left alone. */
      if (!this.select.value) {
        this.clearSwatches();
        this.setReady(this.isReady());
      }
      return;
    }

    var keys = matchKeys(variant);

    this.groups.forEach(function (group) {
      var match = group.swatches.filter(function (swatch) {
        var value = norm(swatch.dataset.optionValue);
        return value && keys.indexOf(value) !== -1;
      })[0];

      if (!match) return;

      group.swatches.forEach(function (item) {
        item.setAttribute('aria-pressed', String(item === match));
      });
      group.selected = match;

      if (group.value) {
        group.value.textContent = match.dataset.swatchName || '';
        group.value.dataset.selected = 'true';
      }
      if (group.desc && match.dataset.swatchDesc) {
        group.desc.textContent = match.dataset.swatchDesc;
      }
    });

    this.setReady(this.isReady());
  };

  /* Put every group back to the copy it rendered with, undoing selectSwatch. */
  BundleHero.prototype.clearSwatches = function () {
    this.groups.forEach(function (group) {
      group.swatches.forEach(function (item) {
        item.setAttribute('aria-pressed', 'false');
      });
      group.selected = null;

      if (group.value) {
        group.value.textContent = group.valuePlaceholder;
        group.value.dataset.selected = 'false';
      }
      if (group.desc) {
        group.desc.textContent = group.descPlaceholder;
        group.desc.classList.remove('is-nudged');
      }
    });
  };

  BundleHero.prototype.isReady = function () {
    // The select rests on a placeholder until a shade is picked, so an empty
    // value means there is no kit to add yet.
    if (this.select && !this.select.value) return false;
    if (!this.groups.length) return true;
    return this.groups.every(function (group) {
      return !group.swatches.length || group.selected;
    });
  };

  BundleHero.prototype.updateKitName = function () {
    if (!this.kitName || !this.select) return;
    var option = this.select.options[this.select.selectedIndex];
    this.kitName.textContent = option ? option.dataset.variantTitle || option.textContent.trim() : '';
  };

  BundleHero.prototype.setReady = function (ready) {
    if (!this.submit) return;

    var label = ready ? this.config.readyLabel : this.config.idleLabel;
    var text = this.submit.querySelector('[data-bh-submit-text]');
    if (label && text) text.textContent = label;

    this.submit.setAttribute('aria-disabled', String(!ready));
  };

  /* --------------------------------------------------------------- cart ---- */

  BundleHero.prototype.initForm = function () {
    var self = this;
    if (!this.form) return;

    this.setReady(this.isReady());

    this.form.addEventListener('submit', function (event) {
      if (!self.isReady()) {
        event.preventDefault();
        self.nudge();
        return;
      }

      // Without fetch support let the native form post to /cart/add.
      if (typeof window.fetch !== 'function') return;

      event.preventDefault();
      self.addToCart();
    });
  };

  /* Point the shopper at the first step they skipped. */
  BundleHero.prototype.nudge = function () {
    var self = this;
    this.reveal();

    var pending = this.groups.filter(function (group) {
      return group.swatches.length && !group.selected;
    })[0];

    if (!pending) {
      /* No swatch group is waiting, so the kit select is the step left undone —
         the case when the dropdown is the only picker in the section. */
      if (this.select && !this.select.value && this.config.nudgeText) {
        this.showError(this.config.nudgeText);
        if (this.select.focus) this.select.focus();
      }
      return;
    }

    var target = pending.desc || pending.value;
    if (target && this.config.nudgeText) {
      if (!target.dataset.original) target.dataset.original = target.textContent;
      target.textContent = this.config.nudgeText;
      target.classList.add('is-nudged');

      window.clearTimeout(this.nudgeTimer);
      this.nudgeTimer = window.setTimeout(function () {
        target.classList.remove('is-nudged');
        target.textContent = target.dataset.original;
      }, 2600);
    }

    if (pending.swatches[0] && pending.swatches[0].scrollIntoView) {
      pending.swatches[0].scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  BundleHero.prototype.buildItems = function () {
    var items = [];
    if (this.select && this.select.value) items.push({ id: Number(this.select.value), quantity: 1 });

    if (this.config.cartMode === 'components' && Array.isArray(this.config.components)) {
      this.config.components.forEach(function (component) {
        if (component && component.id) {
          items.push({ id: Number(component.id), quantity: component.quantity || 1 });
        }
      });
    }

    return items;
  };

  BundleHero.prototype.addToCart = function () {
    var self = this;

    this.clearError();
    this.setBusy(true);

    var items = this.buildItems();
    this.track('add_to_cart', { items: items.length });

    window
      .fetch(this.config.cartAddUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || '');
          return data;
        });
      })
      .then(function () {
        self.setBusy(false);

        /* The theme's public hook: quick-cart refreshes on it and the header
           count re-reads the cart. The section's own floating cart listens for
           it too — see initCart, which is why nothing is refreshed here. */
        document.dispatchEvent(new CustomEvent('apps:product-added-to-cart'));
        self.openCart();
      })
      .catch(function (error) {
        self.setBusy(false);
        self.showError(error.message);
      });
  };

  BundleHero.prototype.setBusy = function (busy) {
    if (!this.submit) return;

    if (busy) {
      this.submit.classList.add('is-busy');
      this.submit.setAttribute('aria-busy', 'true');
    } else {
      this.submit.classList.remove('is-busy');
      this.submit.removeAttribute('aria-busy');
    }
  };

  /* The store's cart is a drawer, so stay on the page and open it — the same
     thing the theme does after its own add-to-cart.

     The theme's "quick-cart:open" event lives on a module-private bus in
     theme.js, but the header cart icon is bound to it, so clicking that icon
     is how we reach it. That icon does not exist on the chromeless bundle
     templates, which is why the cart page stays as the last resort. */
  BundleHero.prototype.openCart = function () {
    // A discount code can only be applied by visiting /discount/CODE, so the
    // components mode still has to navigate — to the cart, never to checkout.
    if (this.config.discountCode) {
      window.location.href =
        this.config.rootUrl +
        'discount/' +
        encodeURIComponent(this.config.discountCode) +
        '?redirect=' +
        encodeURIComponent(this.config.cartUrl);
      return;
    }

    var icon = document.querySelector('[data-js-cart-icon]');
    if (icon && document.querySelector('.quick-cart')) {
      // Same 300ms theme.js waits after its own add, to let the refresh land
      // before the drawer slides in.
      window.setTimeout(function () {
        icon.click();
      }, 300);
      return;
    }

    // No drawer reachable — the cart page is the theme's own fallback too.
    window.location.href = this.config.cartUrl;
  };

  /* ------------------------------------------------ floating cart -------- */

  /* The section carries its own cart CTA because these templates render
     without the site header (see `chromeless_suffixes` in theme.liquid). */
  BundleHero.prototype.initCart = function () {
    var self = this;

    if (this.cartLink) {
      this.cartLink.addEventListener('click', function (event) {
        // The href is the no-JS fallback; with JS the drawer opens in place.
        event.preventDefault();
        self.openCart();
      });

      this.watchFooter();
    }

    if (!this.cartCount) return;

    /* Every hero instance fires this after its own add. Listening rather than
       refreshing inline matters because the buy form shoppers actually reach
       is in the bundle-shade-picker section, which renders no cart button of
       its own — so its add still has to move this badge. */
    document.addEventListener('apps:product-added-to-cart', function () {
      self.refreshCartCount();
    });

    /* The theme dispatches this on document for every cart change it makes,
       quick-cart edits included, and carries the new cart in the detail. */
    document.addEventListener('cart:updated', function (event) {
      var cart = event.detail && event.detail.cart;
      if (cart && typeof cart.item_count === 'number') self.setCartCount(cart.item_count);
    });

  };

  BundleHero.prototype.refreshCartCount = function () {
    var self = this;
    if (!this.cartCount || typeof window.fetch !== 'function') return;

    window
      .fetch(this.config.cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        self.setCartCount(cart.item_count);
      })
      .catch(function () {
        /* a stale badge is not worth surfacing an error over */
      });
  };

  /* The pill itself is always there; only the badge waits for a first item. */
  BundleHero.prototype.setCartCount = function (count) {
    if (!this.cartCount) return;

    this.cartCount.textContent = count;
    if (count > 0) this.cartCount.removeAttribute('hidden');
    else this.cartCount.setAttribute('hidden', 'hidden');
  };

  /* Hide the pill once the page's last section is on screen, so it never
     covers the legal footer — on every device, since it is the same fixed
     button at every width.

     "Last section" is resolved from the DOM rather than named, so it keeps
     working if the sections are reordered. Shopify omits disabled sections
     entirely, which is why the template's `main-page` never shows up here;
     the height check is for a section that renders but is empty. */
  BundleHero.prototype.watchFooter = function () {
    var self = this;
    if (!this.cartLink || typeof window.IntersectionObserver !== 'function') return;

    var main = document.querySelector('main');
    if (!main) return;

    var sections = Array.prototype.slice.call(main.children).filter(function (node) {
      return node.id && node.id.indexOf('shopify-section-') === 0;
    });

    var footer = null;
    for (var i = sections.length - 1; i >= 0; i--) {
      if (sections[i].offsetHeight > 0) {
        footer = sections[i];
        break;
      }
    }

    // Nothing below this section to make way for: on a page that is only the
    // hero, watching it would hide the button the entire time.
    if (!footer || footer.contains(this.root)) return;

    var observer = new window.IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        self.cartLink.classList.toggle('is-hidden', entry.isIntersecting);
      });
    });

    observer.observe(footer);
  };

  BundleHero.prototype.showError = function (message) {
    if (!this.errorNode) return;
    this.errorNode.textContent = message || this.config.genericError || '';
  };

  BundleHero.prototype.clearError = function () {
    if (this.errorNode) this.errorNode.textContent = '';
  };

  /* Fires Meta Pixel / GA4 only if the store already loaded them. */
  BundleHero.prototype.track = function (event, params) {
    try {
      if (typeof window.fbq === 'function') {
        var map = { add_to_cart: 'AddToCart', shade_selected: 'CustomizeProduct' };
        if (map[event]) window.fbq('track', map[event], params);
        else window.fbq('trackCustom', event, params);
      }
      if (typeof window.gtag === 'function') window.gtag('event', event, params);
    } catch (error) {
      /* analytics must never break add-to-cart */
    }
  };

  /* ---------------------------------------------------------------- init ---- */

  function init(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(function (root) {
      if (root.dataset.bhReady === 'true') return;
      root.dataset.bhReady = 'true';
      new BundleHero(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
